/**
 * LicensedSourcesSettings.tsx — Credential management for licensed repair data sources.
 *
 * Allows shop owners to store encrypted credentials for the vendor / AutoZone Pro.
 * Credentials are encrypted with AES-256-GCM and stored ONLY on this shop instance.
 * They are NEVER transmitted to the Hub, other shops, or any external service.
 *
 * The form calls POST /api/ai-settings/credentials/:vendor (shop-local endpoint).
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Typography,
  Space,
  Tag,
  Alert,
  Divider,
  Row,
  Col,
  Spin,
} from 'antd';
import { LockOutlined, CheckCircleOutlined, CloseCircleOutlined, SafetyOutlined } from '@ant-design/icons';
import api from '../../services/api';

const { Title, Text, Paragraph } = Typography;

interface VendorStatus {
  vendor: string;
  stored: boolean;
  last_set_at: string | null;
  last_used_at: string | null;
}

interface VendorConfig {
  id: 'alldata' | 'autozone_pro';
  label: string;
  description: string;
  loginUrl: string;
}

const VENDORS: VendorConfig[] = [
  {
    id: 'alldata',
    label: 'RepairData',
    description: 'OEM repair procedures, wiring diagrams, fluid capacities, and specifications.',
    loginUrl: 'https://www.alldata.com',
  },
  {
    id: 'autozone_pro',
    label: 'AutoZone Pro',
    description: 'Repair information and procedures from AutoZone Professional.',
    loginUrl: 'https://pro.autozone.com',
  },
];

async function fetchVendorStatus(vendorId: string): Promise<VendorStatus> {
  const res = await api.get(`/ai-settings/credentials/${vendorId}/status`);
  return res.data as VendorStatus;
}

async function saveCredentials(
  vendorId: string,
  username: string,
  password: string
): Promise<void> {
  await api.post(`/ai-settings/credentials/${vendorId}`, { username, password });
}

interface VendorCardProps {
  vendor: VendorConfig;
}

const VendorCard: React.FC<VendorCardProps> = ({ vendor }) => {
  const [form] = Form.useForm();
  const [status, setStatus] = useState<VendorStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    loadStatus();
  }, [vendor.id]);

  const loadStatus = async () => {
    setStatusLoading(true);
    try {
      const s = await fetchVendorStatus(vendor.id);
      setStatus(s);
    } catch {
      setStatus({ vendor: vendor.id, stored: false, last_set_at: null, last_used_at: null });
    } finally {
      setStatusLoading(false);
    }
  };

  const handleSave = async (values: { username: string; password: string }) => {
    setSaving(true);
    setSaveSuccess(false);
    setSaveError(null);
    try {
      await saveCredentials(vendor.id, values.username, values.password);
      setSaveSuccess(true);
      form.resetFields(['password']);
      await loadStatus();
    } catch (e: any) {
      setSaveError(e?.response?.data?.error ?? e?.message ?? 'Failed to save credentials');
    } finally {
      setSaving(false);
    }
  };

  const StatusBadge = () => {
    if (statusLoading) return <Spin size="small" />;
    if (status?.stored) {
      return (
        <Tag
          icon={<CheckCircleOutlined />}
          color="success"
          data-testid={`${vendor.id}-status-badge`}
        >
          Credentials stored
        </Tag>
      );
    }
    return (
      <Tag
        icon={<CloseCircleOutlined />}
        color="default"
        data-testid={`${vendor.id}-status-badge`}
      >
        Not configured
      </Tag>
    );
  };

  return (
    <Card
      size="small"
      title={
        <Space>
          <LockOutlined />
          <Text strong>{vendor.label}</Text>
          <StatusBadge />
        </Space>
      }
      style={{ marginBottom: 16 }}
      data-testid={`${vendor.id}-card`}
    >
      <Paragraph type="secondary" style={{ marginBottom: 12, fontSize: 13 }}>
        {vendor.description}
      </Paragraph>

      {status?.stored && status.last_set_at && (
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
          Last updated: {new Date(status.last_set_at).toLocaleDateString()}
          {status.last_used_at && (
            <> &middot; Last used: {new Date(status.last_used_at).toLocaleDateString()}</>
          )}
        </Text>
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSave}
        size="small"
        data-testid={`${vendor.id}-form`}
      >
        <Row gutter={12}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="username"
              label="Username / Email"
              rules={[{ required: true, message: 'Username is required' }]}
            >
              <Input
                placeholder="your@email.com"
                data-testid={`${vendor.id}-username-input`}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="password"
              label="Password"
              rules={[{ required: true, message: 'Password is required' }]}
            >
              <Input.Password
                placeholder="••••••••"
                data-testid={`${vendor.id}-password-input`}
              />
            </Form.Item>
          </Col>
        </Row>

        {saveSuccess && (
          <Alert
            type="success"
            message="Credentials saved securely"
            style={{ marginBottom: 8 }}
            showIcon
          />
        )}

        {saveError && (
          <Alert
            type="error"
            message={saveError}
            style={{ marginBottom: 8 }}
            showIcon
          />
        )}

        <Button
          type="primary"
          htmlType="submit"
          loading={saving}
          icon={<LockOutlined />}
          data-testid={`${vendor.id}-save-btn`}
        >
          {status?.stored ? 'Update Credentials' : 'Save Credentials'}
        </Button>
      </Form>
    </Card>
  );
};

/**
 * LicensedSourcesSettings — the full settings page for licensed vendor credentials.
 *
 * Mount inside the shop settings pages (e.g. alongside other Settings components).
 */
const LicensedSourcesSettings: React.FC = () => {
  return (
    <div data-testid="licensed-sources-settings">
      <Title level={4} style={{ marginBottom: 8 }}>
        Licensed Repair Data Sources
      </Title>

      <Alert
        icon={<SafetyOutlined />}
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Your credentials stay on this device — they are never shared"
        description={
          <>
            <Paragraph style={{ margin: '4px 0 0 0', fontSize: 13 }}>
              Credentials are encrypted with AES-256-GCM and stored locally only on this
              shop&apos;s instance. They are <strong>never</strong> sent to the CNF Hub, other
              shops, or any external service. Lookups using your subscription are also{' '}
              <strong>not shared</strong> with the AI knowledge pool.
            </Paragraph>
          </>
        }
      />

      <Divider orientation="left" style={{ fontSize: 13, color: '#888' }}>
        Configure vendor access
      </Divider>

      {VENDORS.map((vendor) => (
        <VendorCard key={vendor.id} vendor={vendor} />
      ))}
    </div>
  );
};

export default LicensedSourcesSettings;
